/**
 * Query-string shapes for the list sort + date controls.
 *
 * Deliberately separate from `ListSortAndDate.tsx`: a module that
 * exports both components and plain helpers breaks React Fast Refresh
 * (and the lint rule that guards it), so the non-component half lives
 * here.
 *
 * Parsed server-side by `apps/list_params.py`:
 *
 *   ?sort=<key>            leading "-" for descending
 *   ?<prefix>_on=today | yesterday | YYYY-MM-DD
 *   ?<prefix>_from=YYYY-MM-DD & ?<prefix>_to=YYYY-MM-DD
 */
export type SortKey =
  | "created"
  | "contacted"
  | "session_created"
  | "session_ended"
  | "session_started"
  | "started"
  | "ended"
  | "scheduled";

/** What the caller holds; an absent field means "not set". */
export interface DateWindow {
  /** "today" | "yesterday" | "YYYY-MM-DD" — wins over from/to. */
  on?: string;
  from?: string;
  to?: string;
}

export const emptyWindow: DateWindow = {};

export function windowIsSet(w: DateWindow | undefined): boolean {
  return Boolean(w && (w.on || w.from || w.to));
}

/** Turn a window into the query params the backend reads. */
export function windowParams(
  prefix: string,
  w: DateWindow | undefined,
): Record<string, string> {
  if (!w) return {};
  if (w.on) return { [`${prefix}_on`]: w.on };
  const out: Record<string, string> = {};
  if (w.from) out[`${prefix}_from`] = w.from;
  if (w.to) out[`${prefix}_to`] = w.to;
  return out;
}

/**
 * Local Y-M-D — NOT `toISOString()`, which converts to UTC first and
 * hands the server yesterday for anyone west of Greenwich in the
 * evening. Dates travel as a plain day; the server resolves it in the
 * campaign's timezone.
 */
export function todayISO(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
